import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  IconButton,
  useTheme,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import DatePicker2 from "../../../../../../views/components/common/textFields/DatePicker2";
import dayjs from "dayjs";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import {
  UnpaidSampleProduct,
  UnpaidSamplesReturnInfoUpdate,
} from "../../../../../../models/ProjectManagement/UnpaidSample";
import { Dayjs } from "dayjs";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import MediaAssetsUpload from "../../../../../../views/components/common/upload/MediaAssetsUpload";
import ImageAPI from "../../../../api/ImageAPI";
import { ImageData } from "../../../../../../models/Image/Image";
import FileUploadIcon from "../../../../../../assets/fileuploadicon.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";

interface UnpaidSamplePickupUpdatePopupProps {
  open: boolean;
  existingPickupData: UnpaidSamplesReturnInfoUpdate | null;
  onClose: () => void;
  onSave: (payload: UnpaidSamplesReturnInfoUpdate, pickup_id: number) => void;
  onDeleteImage: (image: ImageData) => void;
  orderId: number;
}

const skuLineKey = (productId: number, sku?: string | null) =>
  `${productId}|${String(sku ?? "").trim().toUpperCase()}`;

const UnpaidSamplePickupUpdatePopup: React.FC<
  UnpaidSamplePickupUpdatePopupProps
> = ({
  open,
  onClose,
  onSave,
  existingPickupData,
  onDeleteImage,
  orderId,
}) => {
  const theme = useTheme();
  const { projectManagementStore, userStore } = useStores();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const skuLines = useMemo(() => {
    return (
      projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products ??
      existingPickupData?.products ??
      []
    );
  }, [
    projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products,
    existingPickupData?.products,
  ]);

  const sortedSkuLines = useMemo(() => {
    return [...skuLines].sort((a, b) => {
      const aPending = Number(a.pending_quantity ?? 0);
      const bPending = Number(b.pending_quantity ?? 0);
      if (aPending !== bPending) return bPending - aPending;

      const aExpected = Number(a.expected_quantity ?? 0);
      const bExpected = Number(b.expected_quantity ?? 0);
      const aReceived = Number(a.returned_quantity ?? a.quantity ?? 0);
      const bReceived = Number(b.returned_quantity ?? b.quantity ?? 0);
      const aFullyReceived = aExpected > 0 ? aReceived >= aExpected : false;
      const bFullyReceived = bExpected > 0 ? bReceived >= bExpected : false;

      if (aFullyReceived && !bFullyReceived) return 1;
      if (!aFullyReceived && bFullyReceived) return -1;

      const aProduct = Number(a.product_id ?? a.id);
      const bProduct = Number(b.product_id ?? b.id);
      if (aProduct !== bProduct) return aProduct - bProduct;
      return String(a.sku ?? "").localeCompare(String(b.sku ?? ""));
    });
  }, [skuLines]);

  useEffect(() => {
    if (open && existingPickupData) {
      projectManagementStore.setUnpaidSamplePickupUpdateFormData({
        actual_pickup_date: existingPickupData.actual_pickup_date
          ? dayjs(existingPickupData.actual_pickup_date).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          : "",
        actual_pack_received_date: existingPickupData.actual_pack_received_date
          ? dayjs(existingPickupData.actual_pack_received_date).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          : "",
        product_remarks: existingPickupData.product_remarks || "",
        image_urls: existingPickupData.image_urls || [],
      });

      if (skuLines.length > 0) {
        const initialProducts: UnpaidSampleProduct[] = skuLines.map((line) => {
          const expectedQty = Number(line.expected_quantity ?? 0);
          const receivedQty = Number(line.returned_quantity ?? line.quantity ?? 0);
          const defaultReturnQty = Math.max(0, expectedQty - receivedQty);

          return {
            id: line.product_id ?? line.id,
            product_id: line.product_id ?? line.id,
            sku: line.sku ?? "",
            quantity: defaultReturnQty,
            is_selected: false,
          };
        });

        projectManagementStore.setselectedReturnProduct(initialProducts);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingPickupData, skuLines, projectManagementStore]);

  useEffect(() => {
    if (
      open &&
      skuLines.length > 0 &&
      !existingPickupData &&
      projectManagementStore.selectedReturnProduct.length === 0
    ) {
      const initialProducts: UnpaidSampleProduct[] = skuLines.map((line) => {
        const expectedQty = Number(line.expected_quantity ?? 0);
        const receivedQty = Number(line.returned_quantity ?? line.quantity ?? 0);
        const defaultReturnQty = Math.max(0, expectedQty - receivedQty);

        return {
          id: line.product_id ?? line.id,
          product_id: line.product_id ?? line.id,
          sku: line.sku ?? "",
          quantity: defaultReturnQty,
          is_selected: false,
        };
      });

      projectManagementStore.setselectedReturnProduct(initialProducts);

      projectManagementStore.setUnpaidSamplePickupUpdateFormData({
        ...projectManagementStore.unpaidSamplePickupUpdateFormData,
        products: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, skuLines, existingPickupData]);

  const formData = projectManagementStore.unpaidSamplePickupUpdateFormData;

  const getValidationState = () => {
    const hasBasicRequiredFields = formData.actual_pickup_date !== null;
    const hasPackReceivedDate = !!formData.actual_pack_received_date;
    const hasSelectedProducts =
      projectManagementStore.selectedReturnProduct.some(
        (p) => p.is_selected && p.quantity > 0,
      );
    const allSelectedProductsHaveQuantity =
      projectManagementStore.selectedReturnProduct
        .filter((p) => p.is_selected)
        .every((p) => p.quantity > 0);
    const hasAnySelectedProducts =
      projectManagementStore.selectedReturnProduct.some((p) => p.is_selected);
    const hadOriginalPackReceivedDate =
      existingPickupData?.actual_pack_received_date;

    const canSave = hasBasicRequiredFields;

    return {
      hasBasicRequiredFields,
      hasPackReceivedDate,
      hasSelectedProducts,
      allSelectedProductsHaveQuantity,
      hasAnySelectedProducts,
      hadOriginalPackReceivedDate,
      canSave,
    };
  };

  const validationState = getValidationState();

  const handleActualPickupDateChange = (date: Dayjs | null) => {
    formData.actual_pickup_date = date
      ? date.format("YYYY-MM-DD HH:mm:ss")
      : "";
  };

  const handlePackReceivedDateChange = (date: Dayjs | null) => {
    formData.actual_pack_received_date = date
      ? date.format("YYYY-MM-DD HH:mm:ss")
      : "";
  };

  const handleClose = () => {
    projectManagementStore.resetUnpaidSamplePickupUpdateFormData();
    projectManagementStore.setselectedReturnProduct([]);
    onClose();
  };

  const handleRemarksChange = (value: string) => {
    formData.product_remarks = value;
  };

  const existingImageList = React.useMemo(() => {
    if (formData.image_urls && Array.isArray(formData.image_urls)) {
      return formData.image_urls.map((url, index) => ({
        id: url.id,
        title: `Image ${index + 1}`,
        img: url.image_url,
      }));
    }
    return [];
  }, [formData.image_urls]);

  const handleImageUpload = (files: File[]) => {
    // Append new files to existing selectedFiles instead of replacing
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleRemoveLocalImage = (image: {
    id: string | number;
    title?: string;
    img: string;
  }) => {
    if (typeof image.id === "string" && image.id.startsWith("file-")) {
      const fileIndex = parseInt(image.id.replace("file-", ""));

      if (image.img.startsWith("blob:")) {
        URL.revokeObjectURL(image.img);
      }

      const updatedFiles = selectedFiles.filter(
        (_, index) => index !== fileIndex,
      );
      setSelectedFiles(updatedFiles);
    }
  };

  React.useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        const url = URL.createObjectURL(file);
        URL.revokeObjectURL(url);
      });
    };
  }, [selectedFiles]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      {userStore.hasPermission(EmployeeRoles.EDIT_UNPAID_RETURN_SAMPLES) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
              borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <CustomTypography variant="heading3">
              {`Unpaid Sample Post Pickup  (${orderId})`}
            </CustomTypography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: theme.palette.customColors.greyTextColor,
                "&:hover": {
                  backgroundColor: theme.palette.customColors.lightGreyColor,
                  color: theme.palette.customColors.blackBorder,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent
            sx={{
              padding: { xs: "20px", sm: "24px" },
              flex: 1,
              overflow: "auto",
              maxHeight: "60vh",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.customColors.greyBackground,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.customColors.greyTextColor,
                },
              },
            }}
          >
            <Box
              sx={{
                mb: 3,
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1, mb: 3 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Actual Pickup Date{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  The date when the samples were picked up
                </CustomTypography>
                <DatePicker2
                  type="date"
                  label="Select Date"
                  value={
                    formData.actual_pickup_date
                      ? dayjs(formData.actual_pickup_date)
                      : null
                  }
                  onChange={handleActualPickupDateChange}
                  zIndex={4000}
                />
              </Box>

              <Box sx={{ flex: 1, mb: 3 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Pack Received Date
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  The date when the samples were received
                </CustomTypography>
                <DatePicker2
                  type="date"
                  label="Select Date"
                  value={
                    formData.actual_pack_received_date
                      ? dayjs(formData.actual_pack_received_date)
                      : null
                  }
                  onChange={handlePackReceivedDateChange}
                  zIndex={4000}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 4 }}>
              <CustomTypography
                variant="heading5"
                style={{ fontWeight: 600, marginBottom: 10 }}
              >
                Products
                {projectManagementStore.unpaidSamplePickupUpdateFormData
                  .actual_pack_received_date !== null && (
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                )}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                The products that were picked up
              </CustomTypography>

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                  overflowX: "auto",
                }}
              >
                <Table sx={{ tableLayout: "fixed", minWidth: 720 }}>
                  <colgroup>
                    <col style={{ width: 36 }} />
                    <col style={{ width: 44 }} />
                    <col style={{ width: 64 }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: 88 }} />
                    <col style={{ width: 88 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                      }}
                    >
                      <AppTableHeaderCell
                        minWidth="36px"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          width: "36px",
                          minWidth: "36px",
                          padding: "12px 4px 12px 12px",
                        }}
                      ></AppTableHeaderCell>

                      <AppTableHeaderCell
                        minWidth="44px"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          width: "44px",
                          minWidth: "44px",
                          padding: "12px 4px",
                        }}
                      >
                        S.No
                      </AppTableHeaderCell>

                      <AppTableHeaderCell
                        minWidth="64px"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          width: "64px",
                          minWidth: "64px",
                          padding: "12px 8px",
                        }}
                      >
                        ID
                      </AppTableHeaderCell>

                      <AppTableHeaderCell
                        minWidth="0"
                        txtAlign="left"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          width: "24%",
                          minWidth: 0,
                          padding: "12px 12px",
                          textAlign: "left",
                        }}
                      >
                        Product
                      </AppTableHeaderCell>

                      <AppTableHeaderCell
                        minWidth="140px"
                        txtAlign="left"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          textAlign: "left",
                          width: "34%",
                          minWidth: "140px",
                          padding: "12px 12px",
                        }}
                      >
                        SKU
                      </AppTableHeaderCell>

                      <AppTableHeaderCell
                        minWidth="88px"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          textAlign: "center",
                          width: "88px",
                          minWidth: "88px",
                          padding: "12px 8px",
                        }}
                      >
                        Expected
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        minWidth="88px"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          textAlign: "center",
                          width: "88px",
                          minWidth: "88px",
                          padding: "12px 8px",
                        }}
                      >
                        Received
                      </AppTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSkuLines.map((line, index) => {
                      const productId = line.product_id ?? line.id;
                      const receivedQty = Number(
                        line.returned_quantity ?? line.quantity ?? 0,
                      );
                      const expectedQty = Number(line.expected_quantity ?? 0);
                      const isFullyReceived =
                        expectedQty > 0 ? receivedQty >= expectedQty : false;

                      return (
                        <TableRow
                          key={
                            skuLineKey(productId, line.sku) ||
                            `product_${index}`
                          }
                          hover
                        >
                          <AppTableRowCell
                            sx={{
                              width: "36px",
                              minWidth: "36px",
                              padding: "12px 4px 12px 12px",
                            }}
                          >
                            {isFullyReceived && (
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                fontSize={"1.2rem"}
                                style={{
                                  color:
                                    theme.palette.customColors.successGreen,
                                }}
                              />
                            )}
                          </AppTableRowCell>

                          <AppTableRowCell
                            sx={{
                              width: "44px",
                              minWidth: "44px",
                              padding: "12px 4px",
                              textAlign: "center",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {index + 1}
                            </CustomTypography>
                          </AppTableRowCell>

                          <AppTableRowCell
                            sx={{
                              width: "64px",
                              minWidth: "64px",
                              padding: "12px 8px",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {productId || "No product id"}
                            </CustomTypography>
                          </AppTableRowCell>
                          <AppTableRowCell
                            align="left"
                            sx={{
                              width: "24%",
                              minWidth: 0,
                              padding: "10px 12px",
                              verticalAlign: "middle",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              {line.image_url ? (
                                <Box
                                  component="img"
                                  src={line.image_url}
                                  alt=""
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "6px",
                                    objectFit: "cover",
                                    flexShrink: 0,
                                  }}
                                />
                              ) : null}
                              <CustomTypography
                                variant="body2"
                                sx={{
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: 1.35,
                                  textAlign: "left",
                                }}
                              >
                                {line.product_name || "No product name"}
                              </CustomTypography>
                            </Box>
                          </AppTableRowCell>
                          <AppTableRowCell
                            align="left"
                            sx={{
                              textAlign: "left",
                              width: "34%",
                              minWidth: 0,
                              padding: "10px 12px",
                              verticalAlign: "middle",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              sx={{
                                fontSize: "0.8125rem",
                                wordBreak: "break-all",
                                lineHeight: 1.35,
                                textAlign: "left",
                              }}
                            >
                              {line.sku || "—"}
                            </CustomTypography>
                          </AppTableRowCell>
                          <AppTableRowCell
                            sx={{
                              textAlign: "center",
                              width: "88px",
                              minWidth: "88px",
                              padding: "10px 8px",
                              verticalAlign: "middle",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {expectedQty}
                            </CustomTypography>
                          </AppTableRowCell>
                          <AppTableRowCell
                            sx={{
                              textAlign: "center",
                              width: "88px",
                              minWidth: "88px",
                              padding: "10px 8px",
                              verticalAlign: "middle",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {receivedQty}
                            </CustomTypography>
                          </AppTableRowCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider sx={{ my: 3 }} />
            {/* FIXED: Remarks section - now always allows updates */}
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 6 }}
              >
                Remarks
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                Any additional remarks or comments
              </CustomTypography>

              <TextField
                multiline={true}
                rows={3}
                fullWidth
                placeholder="Enter any remarks or additional information..."
                value={formData.product_remarks || ""}
                onChange={(e) => handleRemarksChange(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: theme.palette.customColors.greyDarkborder,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.customColors.blueGradientStart,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.customColors.blueGradientStart,
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Enhanced Image upload section - similar to AddProduct page */}
            <Box>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 6 }}
              >
                Upload Images
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                Upload images related to the pickup
              </CustomTypography>

              <Box sx={{ width: "100%", maxWidth: "100%" }}>
                <MediaAssetsUpload
                  uploadIcon={
                    <img
                      src={FileUploadIcon}
                      alt="Upload"
                      width={40}
                      height={40}
                    />
                  }
                  fileInfo="PNG, JPG up to 2MB each"
                  multiple={true}
                  existingImageList={existingImageList}
                  newImageList={selectedFiles}
                  deleteImage={onDeleteImage}
                  removeImage={handleRemoveLocalImage}
                  addImagesToBeUploaded={handleImageUpload}
                />
              </Box>
            </Box>
          </DialogContent>

          {/* Footer */}
          <DialogActions
            sx={{
              padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              gap: 2,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              {projectManagementStore?.unpaidSamplePickupUpdateInfoResponce
                ?.actual_pickup_modified_by_name && (
                <>
                  <CustomTypography variant="body3">
                    <span style={{ fontWeight: 600 }}>Last Updated by : </span>
                    {projectManagementStore.unpaidSamplePickupUpdateInfoResponce
                      ?.actual_pickup_modified_by_name || "Not yet updated"}
                  </CustomTypography>
                  <CustomTypography variant="body3" style={{ marginBottom: 5 }}>
                    <span style={{ fontWeight: 600 }}>Last Updated on : </span>
                    {projectManagementStore.unpaidSamplePickupUpdateInfoResponce
                      ?.modified_date
                      ? dayjs(
                          projectManagementStore
                            .unpaidSamplePickupUpdateInfoResponce.modified_date,
                        ).format("DD/MM/YYYY HH:mm")
                      : "Not yet updated"}
                  </CustomTypography>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  borderColor: theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.greyTextColor,
                  fontWeight: 600,
                  padding: "10px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.customColors.greyTextColor,
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  },
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={async () => {
                  let uploadedImageUrls: string[] = [];
                  if (selectedFiles.length > 0) {
                    const imageAPI = new ImageAPI();
                    uploadedImageUrls = await imageAPI
                      .uploadImage(selectedFiles, "product-images")
                      .then((res) => {
                        setSelectedFiles([]);
                        return res;
                      });
                  }

                  const selectedProducts =
                    projectManagementStore.selectedReturnProduct
                      .filter((p) => p.is_selected && p.quantity > 0)
                      .map((p) => ({
                        id: p.id,
                        sku: p.sku ?? "",
                        quantity: p.quantity,
                      }));

                  const payload: any = {
                    ...formData,
                    products: selectedProducts,
                  };

                  // Remove image_urls from payload to avoid sending existing images
                  delete payload.image_urls;

                  // Only add image_url if there are new uploaded images
                  if (uploadedImageUrls.length > 0) {
                    payload.image_url = uploadedImageUrls;
                  } else {
                    // When no new images are uploaded, pass empty array
                    payload.image_url = [];
                  }

                  onSave(
                    payload,
                    Number(projectManagementStore.selectedReturnPickupId),
                  );
                }}
                variant="contained"
                disabled={!validationState.canSave}
                sx={{
                  backgroundColor: validationState.canSave
                    ? theme.palette.customColors.EmeraldGreen
                    : theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.white,
                  fontWeight: 600,
                  padding: "10px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: validationState.canSave
                      ? theme.palette.customColors.greenDark
                      : theme.palette.customColors.greyDarkborder,
                    transform: validationState.canSave
                      ? "translateY(-1px)"
                      : "none",
                    boxShadow: validationState.canSave
                      ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                      : "none",
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.customColors.greyDarkborder,
                    color: theme.palette.customColors.white,
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

export default observer(UnpaidSamplePickupUpdatePopup);
